import { motion } from 'framer-motion';
import { FiVideo, FiLock, FiZap, FiTrash2 } from 'react-icons/fi';
import CompactToolCard from '../components/CompactToolCard';
import FeatureCard from '../components/FeatureCard';
import SEO from '../components/SEO';
import { videoToolsSchemas } from '../schemas';
import './ToolPage.css';

const VideoTools = () => {
  const videoTools = [
    { title: 'Video to GIF', fromFormat: 'MP4', toFormat: 'GIF', customLink: '/video-to-gif' },
    { title: 'Video to Audio', fromFormat: 'MP4', toFormat: 'MP3', customLink: '/video-to-audio' },
    { title: 'Remove Audio', fromFormat: 'MP4', toFormat: 'MP4', customLink: '/video-audio-remover' },
    { title: 'Compress Video', fromFormat: 'MP4', toFormat: 'MP4', customLink: '/video-compressor' }
  ];

  const relatedTools = [
    { title: 'JPG → PNG', fromFormat: 'JPG', toFormat: 'PNG' },
    { title: 'PNG → JPG', fromFormat: 'PNG', toFormat: 'JPG' },
    { title: 'Reduce Image Size', fromFormat: 'JPG', toFormat: 'JPG', customLink: '/image-compressor' },
    { title: 'Image Cropper', fromFormat: 'IMG', toFormat: 'CROP', customLink: '/image-cropper' },
    { title: 'Merge PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-merge' },
    { title: 'Split PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-split' },
    { title: 'Compress PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/compress-pdf' },
    { title: 'PDF to Images', fromFormat: 'PDF', toFormat: 'PNG', customLink: '/pdf-to-images' }
  ];

  const features = [
    {
      icon: <FiLock />,
      title: 'Secure & Private',
      description: 'Your files are processed securely and never stored on our servers after processing.'
    },
    {
      icon: <FiZap />,
      title: 'Lightning Fast',
      description: 'GPU-accelerated processing for instant results even on large videos.'
    },
    {
      icon: <FiTrash2 />,
      title: 'Auto-Deleted Files',
      description: 'All uploaded files are automatically deleted from our servers after 2 hours.'
    }
  ];

  return (
    <div className="tool-page">
      <SEO 
        title="Free Video Tools Online – Convert, Compress, Edit"
        description="Convert video to audio/GIF, compress video size, or remove audio online free. Fast, browser-based video tools — no download needed."
        keywords="video tools online, free video editor, compress video online"
        path="/video-tools"
        schema={videoToolsSchemas}
      />
      <section className="page-hero">
        <div className="page-hero-container">
          <motion.div
            className="page-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FiVideo /> VIDEO PROCESSING
          </motion.div>

          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Video Editing & Conversion
          </motion.h1>

          <motion.p
            className="page-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Professional video tools for creators, editors, and enthusiasts. Fast processing and no registration required.
          </motion.p>
        </div>
      </section>

      <section className="section tools-section">
        <div className="section-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Video Tools
          </motion.h2>

          <div className="compact-tools-grid">
            {videoTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CompactToolCard {...tool} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section related-tools-section">
        <div className="section-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Related Tools
          </motion.h2>

          <div className="compact-tools-grid">
            {relatedTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <CompactToolCard {...tool} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section features-section">
        <div className="section-container">
          <motion.h2
            className="section-title centered"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why use Multiverse Video Tools?
          </motion.h2>

          <div className="features-grid">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} delay={index * 0.15} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VideoTools;
